/**
 * Web Push REST service.
 *
 * Typed bridge to the backend's /api/v1/webpush/* endpoints. Plumbing only —
 * no logic. The subscribe orchestration (permission prompt, key conversion,
 * PushManager.subscribe) lives in hooks/useWebPushNotification.ts.
 */

import { apiRequest } from "./api.client";

// GET /webpush/vapid-public-key
interface VapidPublicKeyResponse {
  public_key: string;
}

// POST /webpush/subscriptions body.
// Backend wants snake_case p256dh_key / auth_key. The browser gives
// { endpoint, keys: { p256dh, auth } } — that mapping happens in the hook,
// not here; this file stays a 1:1 wrapper.
export interface SaveSubscriptionPayload {
  endpoint: string;
  p256dh_key: string;
  auth_key: string;
  user_agent?: string;
}

// What POST + GET-list return (backend PushSubscriptionOut).
// Intentionally NO endpoint / p256dh_key / auth_key — those are write-only
// on the backend and never sent back (the endpoint URL is a delivery credential).
export interface WebPushDevice {
  subscription_id: string;
  user_agent: string | null;
  created_at: string;        // ISO datetime
  last_used_at: string | null;
}

interface DeviceListResponse {
  items: WebPushDevice[];
}

export const webPushNotificationService = {
  async getVapidPublicKey(): Promise<string> {
    const res = await apiRequest<VapidPublicKeyResponse>(
      "/webpush/vapid-public-key",
    );
    return res.public_key;
  },

  async saveSubscription(payload: SaveSubscriptionPayload): Promise<WebPushDevice> {
    return apiRequest<WebPushDevice>("/webpush/subscriptions", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async listDevices(): Promise<WebPushDevice[]> {
    const res = await apiRequest<DeviceListResponse>("/webpush/subscriptions");
    return res.items;
  },

  async deleteSubscription(subscriptionId: string): Promise<void> {
    await apiRequest<void>(`/webpush/subscriptions/${subscriptionId}`, {
      method: "DELETE",
    });
  },
};
